"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isWhiteSpaceOrPunctuation = isWhiteSpaceOrPunctuation;
exports.lastIndexOfRegex = lastIndexOfRegex;
exports.getNthOccurrenceOfSubString = getNthOccurrenceOfSubString;
exports.replaceIDWithName = replaceIDWithName;
exports.replaceNameWithID = replaceNameWithID;
exports.loadFromLocalStorage = loadFromLocalStorage;
exports.saveToLocalStorage = saveToLocalStorage;
exports.deleteFromLocalStorage = deleteFromLocalStorage;
exports.loadFromSessionStorage = loadFromSessionStorage;
exports.saveToSessionStorage = saveToSessionStorage;
exports.deleteFromSessionStorage = deleteFromSessionStorage;
exports.actionableKeyPress = actionableKeyPress;
exports.normalizeIndex = normalizeIndex;
exports.isValidEmail = isValidEmail;
exports.isKeyboardSubmit = void 0;

var _constants = require("../constants");

/***************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

/**
 * Determine if a character is whitespace or empty ''
 * @param {string} char - string to be tested
 * @returns {boolean} is white space
 */
function isWhiteSpaceOrPunctuation(char) {
  return /\s/.test(char) || _constants.MENTION_PUNCTUATION.indexOf(char) > -1;
}
/**
 * Find the last index of a regex expression in a string
 * @param {string} string - string to be tested
 * @param {RegExp} regex - regular expression tested against string
 * @returns {number} last index of regex
 */


function lastIndexOfRegex(string, regex) {
  var searchString = string;
  var lastIndex = searchString.search(regex);
  var tempIndex = lastIndex;

  if (tempIndex === -1) {
    return -1;
  }

  while (tempIndex !== -1) {
    searchString = searchString.slice(tempIndex + 1);
    tempIndex = searchString.search(regex);
    lastIndex += tempIndex + 1;
  }

  return lastIndex;
}
/**
 * Find the nth occurrence of a substring
 * @param {string} string - array of chars to be tested
 * @param {string} subString - array of characters to be tested against for matches
 * @param {number} occurrence - the number of occurrences
 * @returns {number} - index of matched substring
 */


function getNthOccurrenceOfSubString(string, subString, occurrence) {
  var idx = 0;
  var occuranceIndex = occurrence;

  while (idx !== -1 && occuranceIndex > 0) {
    occuranceIndex--;
    idx = string.indexOf(subString, idx + 1);
  }

  return idx;
}
/**
 * Replace IDs in text with user names
 * @param {string} text - text to replace IDs with user's name
 * @param {Array} userObjects - array of user objects
 * @returns {string} - text with user names instead of IDs
 */


function replaceIDWithName(text) {
  var userObjects = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var idx;
  var startIdx = 0;
  var correctedText = text;
  var lowerCaseText = text.toLocaleLowerCase();
  var lowerCaseUserId;
  var lowerCaseStrippedName;
  var testString;
  userObjects.forEach(function (user) {
    var id = user.id.replace('urn:uuid:', '');
    lowerCaseUserId = "[@urn:userID:".concat(id, "]").toLocaleLowerCase();
    lowerCaseStrippedName = "@".concat(user.name.replace(/ /g, '').toLocaleLowerCase()); // Because of a bug with Comments Service or Courier we have to check for both AdobeID
    // and user names where spaces have been stripped.

    idx = lowerCaseText.indexOf(lowerCaseUserId);
    testString = lowerCaseUserId; // If no adobeID has been found for user use their name with spaces have been stripped.

    if (idx === -1) {
      idx = lowerCaseText.indexOf(lowerCaseStrippedName);
      testString = lowerCaseStrippedName;
    }

    while (idx !== -1) {
      if (isWhiteSpaceOrPunctuation(correctedText.charAt(idx - 1)) && isWhiteSpaceOrPunctuation(correctedText.charAt(idx + testString.length))) {
        correctedText = "".concat(correctedText.slice(startIdx, idx), "@").concat(user.name).concat(correctedText.slice(idx + testString.length));
      }

      startIdx = 0;
      idx = correctedText.toLocaleLowerCase().indexOf(testString, idx + user.name.length);
    }

    lowerCaseText = correctedText.toLocaleLowerCase();
  });
  return correctedText;
}
/**
 * Replace names in text with IDs
 * @param {string} text - text to replace names with user's id
 * @param {Array} userObjects - array of users
 * @returns {string} - text with IDs instead of names
 */


function replaceNameWithID(text) {
  var userObjects = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var idx;
  var startIdx = 0;
  var correctedText = text;
  var lowerCaseText = text.toLocaleLowerCase();
  var lowerCaseUsername;
  userObjects.forEach(function (user) {
    var id = user.id.replace('urn:uuid:', '');
    lowerCaseUsername = "@".concat(user.name).toLocaleLowerCase();
    idx = lowerCaseText.indexOf(lowerCaseUsername);

    while (idx !== -1) {
      if (isWhiteSpaceOrPunctuation(correctedText.charAt(idx - 1)) && isWhiteSpaceOrPunctuation(correctedText.charAt(idx + lowerCaseUsername.length))) {
        correctedText = "".concat(correctedText.slice(startIdx, idx), "[@urn:userID:").concat(id, "]").concat(correctedText.slice(idx + lowerCaseUsername.length));
      }

      startIdx = 0;
      idx = correctedText.toLocaleLowerCase().indexOf(lowerCaseUsername, idx + id.length);
    }

    lowerCaseText = correctedText.toLocaleLowerCase();
  });
  return correctedText;
}
/**
 * Returns data from local storage for given key.
 * @param {string} key - key used to look up the item
 * @return {Object|undefined} deserialized object or undefined if data or localStorage isn't available
 */


function loadFromLocalStorage(key) {
  try {
    var serializedData = localStorage.getItem(key);

    if (serializedData === null) {
      return undefined;
    }

    return JSON.parse(serializedData);
  } catch (error) {
    return undefined;
  }
}
/**
 * Saves data to local storage for given key.
 * @param {string} key - key associated with the given value
 * @param {Object} value - value to save
 * @return {undefined}
 */


function saveToLocalStorage(key, value) {
  try {
    var serializedData = JSON.stringify(value);
    localStorage.setItem(key, serializedData);
  } catch (error) {// prevent app from crashing
  }
}
/**
 * Removes an entry from local storage
 * @param {string} key - key used to look up the item
 * @return {undefined}
 */


function deleteFromLocalStorage(key) {
  if (key) {
    localStorage.removeItem(key);
  }
}
/**
 * Returns data from session storage for given key.
 * @param {string} key - key used to look up the item
 * @return {Object|undefined} deserialized object or undefined if data or sessionStorage isn't available
 */


function loadFromSessionStorage(key) {
  try {
    var serializedData = sessionStorage.getItem(key);

    if (serializedData === null) {
      return undefined;
    }

    return JSON.parse(serializedData);
  } catch (error) {
    return undefined;
  }
}
/**
 * Saves data to session storage for given key.
 * @param {string} key - key associated with the given value
 * @param {Object} value - value to save
 * @return {undefined}
 */


function saveToSessionStorage(key, value) {
  try {
    var serializedData = JSON.stringify(value);
    sessionStorage.setItem(key, serializedData);
  } catch (error) {// prevent app from crashing
  }
}
/**
 * Removes an entry from session storage
 * @param {string} key - key used to look up the item
 * @return {undefined}
 */


function deleteFromSessionStorage(key) {
  if (key) {
    sessionStorage.removeItem(key);
  }
}
/**
 * Helper function for keyboard accessibility to detect space or enter.
 * @param {SyntheticEvent} evt - event from keypress
 * @returns {boolean} if user hit space or enter
 */


function actionableKeyPress() {
  var evt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  if (evt.type !== 'keyup') {
    return true;
  }

  return evt.keyCode === _constants.KEY_CODES.ENTER || evt.keyCode === _constants.KEY_CODES.SPACE;
}
/**
 * Function to return a normalized index for an array
 * @param {number} selectedIndex - requested index
 * @param {number} max - highest valid index
 * @return {number} normalized index based on a max
 */


function normalizeIndex(selectedIndex, max) {
  var index = selectedIndex % max;

  if (index < 0) {
    index += max;
  }

  return index;
}
/**
 * Checks is a string is a valid email address
 *
 * @param {string} checkString the string to check for a valid email
 * @returns {boolean} Whether the checkString string is valid or not
 */


function isValidEmail() {
  var checkString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var emailPattern = new RegExp( // eslint-disable-next-line no-control-regex
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/);
  return emailPattern.test(checkString.toLowerCase());
}

var isKeyboardSubmit = function isKeyboardSubmit(evt) {
  var SHORTKEY = /Mac/i.test(navigator.platform) ? 'metaKey' : 'ctrlKey';
  return evt.keyCode === _constants.KEY_CODES.ENTER && evt[SHORTKEY];
};

exports.isKeyboardSubmit = isKeyboardSubmit;